import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  ReceiptIcon,
  TrendingUpIcon,
  StoreIcon,
  PlusIcon,
  ArrowRightIcon,
  Loader2Icon,
  RepeatIcon,
  FolderIcon,
  ListIcon,
  SettingsIcon,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtTRY = (n) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(Number(n || 0));

const fmtTRYFull = (n) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(Number(n || 0));

/**
 * SoloDashboardCards — Solo-plan dashboard widgets that replace the team /
 * advance-flow cards on the main Dashboard (Son Aktiviteler / Projelere Göre
 * Giderler / Aylık Harcama Trendi).
 *
 * Data source: `GET /api/solo/dashboard-stats`. One request fills four
 * panels: KPI strip, last-5 expenses, top-5 suppliers bar list, and the
 * last-6-months mini bar chart. No chart library — pure CSS bars.
 */
const SoloDashboardCards = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${BACKEND_URL}/api/solo/dashboard-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStats(r.data);
      } catch (e) {
        setStats({
          recent_expenses: [],
          top_suppliers: [],
          monthly_trend: [],
          summary: { this_month_total: 0, this_year_total: 0, all_time_total: 0, all_time_count: 0, recurring_count: 0 },
        });
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <Card className="p-10 flex items-center justify-center gap-2 text-gray-400">
        <Loader2Icon className="w-4 h-4 animate-spin" /> Yükleniyor...
      </Card>
    );
  }

  const summary = stats?.summary || {};
  const recent = stats?.recent_expenses || [];
  const top = stats?.top_suppliers || [];
  const trend = stats?.monthly_trend || [];
  const topPeak = Math.max(...top.map((s) => s.total), 0);
  const trendPeak = Math.max(...trend.map((m) => m.total), 0);

  return (
    <div className="space-y-6" data-testid="solo-dashboard-cards">
      {/* KPI strip — replaces the advance-flow KPI row that's hidden for Solo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3" data-testid="solo-kpi-row">
        <Card className="p-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Bu Ay</div>
          <div className="text-2xl font-bold text-gray-900 mt-1" data-testid="solo-kpi-this-month">
            {fmtTRYFull(summary.this_month_total)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Bu Yıl</div>
          <div className="text-2xl font-bold text-gray-900 mt-1" data-testid="solo-kpi-this-year">
            {fmtTRYFull(summary.this_year_total)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Toplam Kayıt</div>
          <div className="text-2xl font-bold text-gray-900 mt-1" data-testid="solo-kpi-total-count">
            {summary.all_time_count}
            <span className="text-sm font-medium text-gray-400 ml-1">×</span>
          </div>
        </Card>
        <Card className="p-4 bg-indigo-50/40 border-indigo-100">
          <div className="text-xs font-medium text-indigo-700 uppercase tracking-wide flex items-center gap-1">
            <RepeatIcon className="w-3.5 h-3.5" /> Düzenli Ödeme
          </div>
          <div className="text-2xl font-bold text-indigo-900 mt-1" data-testid="solo-kpi-recurring">
            {summary.recurring_count}
            <span className="text-sm font-medium text-indigo-400 ml-1">firma</span>
          </div>
        </Card>
      </div>

      {/* Row 1: Son Harcamalarım + Hızlı İşlemler side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-4 sm:p-6" data-testid="solo-recent-expenses">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ReceiptIcon className="w-5 h-5 text-purple-600" /> Son Harcamalarım
            </h3>
            <Link
              to="/dashboard/expenses"
              className="text-xs text-purple-700 hover:text-purple-900 inline-flex items-center gap-1"
            >
              Tümü <ArrowRightIcon className="w-3 h-3" />
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="py-8 text-center text-gray-400">
              <ReceiptIcon className="w-10 h-10 text-gray-200 mx-auto mb-2" />
              <p className="text-sm">Henüz harcama kaydınız yok.</p>
              <Link
                to="/dashboard/expenses/new"
                className="inline-flex items-center gap-1 mt-2 text-sm text-purple-700 hover:text-purple-900"
              >
                <PlusIcon className="w-3.5 h-3.5" /> İlk harcamanı ekle
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recent.map((e) => (
                <div key={e.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {e.supplier || '—'}
                      <span
                        className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${
                          e.expense_type === 'personal'
                            ? 'bg-blue-50 text-blue-700'
                            : 'bg-emerald-50 text-emerald-700'
                        }`}
                      >
                        {e.expense_type === 'personal' ? 'Kişisel' : 'Şirket'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {e.date} · {e.category || 'Kategori yok'}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-gray-900 tabular-nums whitespace-nowrap">
                    {fmtTRYFull(e.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Hızlı İşlemler</h3>
          <div className="grid grid-cols-2 gap-4" data-testid="quick-actions-solo">
            <Link to="/dashboard/expenses/new">
              <Button className="btn-primary w-full h-20 flex-col" data-testid="quick-new-expense">
                <PlusIcon className="w-6 h-6 mb-1" />
                <span>Yeni Harcama</span>
              </Button>
            </Link>
            <Link to="/dashboard/suppliers">
              <Button variant="outline" className="w-full h-20 flex-col border-2 hover:bg-gray-50" data-testid="quick-suppliers">
                <FolderIcon className="w-6 h-6 mb-1" />
                <span>İşletmeler</span>
              </Button>
            </Link>
            <Link to="/dashboard/expenses">
              <Button variant="outline" className="w-full h-20 flex-col border-2 hover:bg-gray-50" data-testid="quick-all-expenses">
                <ListIcon className="w-6 h-6 mb-1" />
                <span>Tüm Harcamalarım</span>
              </Button>
            </Link>
            <Link to="/dashboard/settings">
              <Button variant="outline" className="w-full h-20 flex-col border-2 hover:bg-gray-50" data-testid="quick-settings">
                <SettingsIcon className="w-6 h-6 mb-1" />
                <span>Ayarlar</span>
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      {/* Row 2: Top suppliers bar chart + last-6-months trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-4 sm:p-6" data-testid="solo-top-suppliers">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <StoreIcon className="w-5 h-5 text-purple-600" /> İşletmelere Göre Dağılım
            </h3>
            <span className="text-xs text-gray-500">Top 5 firma</span>
          </div>
          {top.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm">Henüz işletme verisi yok.</div>
          ) : (
            <div className="space-y-3">
              {top.map((s, i) => {
                const pct = topPeak > 0 ? (s.total / topPeak) * 100 : 0;
                const palette = ['bg-purple-500', 'bg-purple-400', 'bg-purple-300', 'bg-purple-200', 'bg-purple-100'];
                return (
                  <div key={s.name} data-testid={`solo-top-supplier-${i}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium text-gray-900 truncate">{s.name}</span>
                      <span className="tabular-nums text-gray-700">
                        {fmtTRY(s.total)}
                        <span className="text-xs text-gray-400 ml-1">· {s.count}×</span>
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${palette[i] || 'bg-purple-200'} rounded-full transition-all`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-4 sm:p-6" data-testid="solo-monthly-trend">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUpIcon className="w-5 h-5 text-purple-600" /> Aylık Solo Harcama Trendi
            </h3>
            <span className="text-xs text-gray-500">Son 6 ay</span>
          </div>
          {trendPeak === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm">Son 6 ayda harcama yok.</div>
          ) : (
            <>
              <div className="grid grid-cols-6 gap-2 h-44 items-end">
                {trend.map((m, i) => {
                  const heightPct = trendPeak > 0 ? Math.max(2, (m.total / trendPeak) * 100) : 2;
                  const isEmpty = m.total === 0;
                  return (
                    <div key={m.ym} className="relative flex flex-col items-center justify-end h-full">
                      <div
                        className={`w-full rounded-t transition-all ${isEmpty ? 'bg-gray-100' : 'bg-purple-500'}`}
                        style={{ height: `${heightPct}%`, minHeight: isEmpty ? '4px' : '8px' }}
                        title={`${m.label}: ${fmtTRYFull(m.total)}`}
                        data-testid={`solo-trend-bar-${i}`}
                      />
                      {!isEmpty && (
                        <div
                          className="absolute text-[10px] font-medium text-gray-700 whitespace-nowrap"
                          style={{ bottom: `calc(${heightPct}% + 4px)` }}
                        >
                          {fmtTRY(m.total)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-6 gap-2 mt-2">
                {trend.map((m) => (
                  <div key={m.ym} className="text-[11px] text-center text-gray-600">
                    {m.label}
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SoloDashboardCards;
